import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  type DocumentReference,
  type CollectionReference,
  type Query,
  type DocumentData,
  type QuerySnapshot,
  type DocumentSnapshot,
  type SetOptions,
  type UpdateData,
  type Firestore
} from 'firebase/firestore';
import { withRetry, type RetryConfig } from './firestoreRetry';
import { withOfflineDetection } from './offlineHandler';

/**
 * Default retry configuration for Firestore operations
 */
const DEFAULT_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2
};

/**
 * Get a document with retry logic and offline detection
 */
export async function getDocumentWithRetry<T = DocumentData>(
  docRef: DocumentReference<T>,
  config?: RetryConfig
): Promise<DocumentSnapshot<T>> {
  return withOfflineDetection(() =>
    withRetry(() => getDoc(docRef), config || DEFAULT_CONFIG)
  );
}

/**
 * Get multiple documents with retry logic and offline detection
 */
export async function getDocumentsWithRetry<T = DocumentData>(
  queryRef: Query<T> | CollectionReference<T>,
  config?: RetryConfig
): Promise<QuerySnapshot<T>> {
  return withOfflineDetection(() =>
    withRetry(() => getDocs(queryRef), config || DEFAULT_CONFIG)
  );
}

/**
 * Set a document with retry logic and offline detection
 */
export async function setDocumentWithRetry<T = DocumentData>(
  docRef: DocumentReference<T>,
  data: T,
  options?: SetOptions,
  config?: RetryConfig
): Promise<void> {
  return withOfflineDetection(() =>
    withRetry(
      () => (options ? setDoc(docRef, data, options) : setDoc(docRef, data)),
      config || DEFAULT_CONFIG
    )
  );
}

/**
 * Update a document with retry logic and offline detection
 */
export async function updateDocumentWithRetry<T = DocumentData>(
  docRef: DocumentReference<T>,
  data: UpdateData<T>,
  config?: RetryConfig
): Promise<void> {
  return withOfflineDetection(() =>
    withRetry(() => updateDoc(docRef, data as any), config || DEFAULT_CONFIG)
  );
}

/**
 * Delete a document with retry logic and offline detection
 */
export async function deleteDocumentWithRetry<T = DocumentData>(
  docRef: DocumentReference<T>,
  config?: RetryConfig
): Promise<void> {
  return withOfflineDetection(() =>
    withRetry(() => deleteDoc(docRef), config || DEFAULT_CONFIG)
  );
}

/**
 * Add a document to a collection with retry logic and offline detection
 */
export async function addDocumentWithRetry<T = DocumentData>(
  collectionRef: CollectionReference<T>,
  data: T,
  config?: RetryConfig
): Promise<DocumentReference<T>> {
  return withOfflineDetection(() =>
    withRetry(() => addDoc(collectionRef, data), config || DEFAULT_CONFIG)
  );
}

/**
 * Convenience function to create a collection reference
 */
export function getCollectionRef(
  db: Firestore,
  collectionPath: string
): CollectionReference {
  return collection(db, collectionPath);
}

/**
 * Convenience function to create a document reference
 */
export function getDocRef(
  db: Firestore,
  collectionPath: string,
  docId: string
): DocumentReference {
  return doc(db, collectionPath, docId);
}

/**
 * Example usage documentation
 * 
 * @example
 * ```typescript
 * import { db } from './firebase/config';
 * import { getDocumentWithRetry, getDocRef } from './utils/firestoreOperations';
 * 
 * // Get a document with automatic retry
 * const userRef = getDocRef(db, 'users', userId);
 * const userDoc = await getDocumentWithRetry(userRef);
 * 
 * if (userDoc.exists()) {
 *   const userData = userDoc.data();
 *   console.log('User data:', userData);
 * }
 * ```
 * 
 * @example
 * ```typescript
 * import { db } from './firebase/config';
 * import { getDocumentsWithRetry, getCollectionRef } from './utils/firestoreOperations';
 * import { query, where } from 'firebase/firestore';
 * 
 * // Get documents with a query
 * const ordersRef = getCollectionRef(db, 'orders');
 * const q = query(ordersRef, where('userId', '==', userId));
 * const ordersSnapshot = await getDocumentsWithRetry(q);
 * 
 * const orders = ordersSnapshot.docs.map(doc => ({
 *   id: doc.id,
 *   ...doc.data()
 * }));
 * ```
 * 
 * @example
 * ```typescript
 * import { db } from './firebase/config';
 * import { setDocumentWithRetry, getDocRef } from './utils/firestoreOperations';
 * 
 * // Set a document with custom retry config
 * const orderRef = getDocRef(db, 'orders', orderId);
 * await setDocumentWithRetry(
 *   orderRef,
 *   orderData,
 *   undefined, // options
 *   { maxRetries: 5, initialDelay: 2000 } // custom config
 * );
 * ```
 */
